import { useEffect } from "react";
import { PERSONAL_VAULT_CONSTANTS } from "../_MODULES/_API";
import { OPTIONS_VAULT_KIND, PALETTE_HEX } from "../../../_Catalogs.Views";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FormGrid, FieldInput, FieldTextarea, FieldSelect, FieldColor, FieldRichText, FieldSwitch, useForm } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { isDuplicateRecord } from "../../../../_UI/recordUniqueness";

const EMPTY_VALUES = {
    name: "", kind: "SERVICE", description: "", url: "", color: "#e83a52", accessPin: "", notes: "", isFavorite: false,
};

export const PersonalVaultGroupFormModal = ({ isOpen, group = null, groups = [], onClose, onSaved }) => {

    const IS_EDIT = !!group?.id;

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 400,
    });

    const FORM = useForm({
        initialValues: EMPTY_VALUES,
        schema: { name: { required: true, minLength: 2 } },

        onSubmit: async (values) => {
            if (isDuplicateRecord({ rows: groups, value: values.name, currentId: group?.id })) {
                FORM.setError("name", "Ya existe un grupo con ese nombre", { focus: true });
                return;
            }
            const PAYLOAD = {
                name: values.name.trim(),
                kind: values.kind,
                description: values.description || "",
                url: values.url || "",
                color: values.color || undefined,
                notes: values.notes || "",
                isFavorite: !!values.isFavorite,
            };

            if (values.accessPin) PAYLOAD.accessPin = values.accessPin;

            const RESPONSE = IS_EDIT
                ? await actionMutate({
                    method: "PATCH", endpoint: PERSONAL_VAULT_CONSTANTS.GROUP_UPDATE,
                    successMessage: "Grupo personal actualizado", id: group.id, data: PAYLOAD,
                })
                : await actionMutate({
                    method: "POST", endpoint: PERSONAL_VAULT_CONSTANTS.GROUP_CREATE,
                    successMessage: "Grupo creado en la bóveda personal", data: PAYLOAD,
                });

            if (!RESPONSE.success) { FORM.applyApiError(RESPONSE); return; }

            onSaved?.(RESPONSE.data, IS_EDIT);
            onClose?.();
        },
    });

    useEffect(() => {
        if (!isOpen) return;

        FORM.setValues(IS_EDIT ? {
            name: group.name ?? "",
            kind: group.kind ?? "SERVICE",
            description: group.description ?? "",
            url: group.url ?? "",
            color: group.color ?? "#e83a52",
            accessPin: "",
            notes: group.notes ?? "",
            isFavorite: !!group.isFavorite,
        } : EMPTY_VALUES);
    }, [isOpen, group]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <AppModal
            isOpen={isOpen}
            onClose={onClose}
            isDirty={FORM.isDirty}
            size="lg"
            title={IS_EDIT ? "Editar grupo personal" : "Nuevo grupo personal"}
            description="Organizá cuentas y accesos propios sin relacionarlos con compañías ni proyectos."
            footer={
                <>
                    <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                    <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>
                        {IS_EDIT ? "Guardar cambios" : "Crear grupo"}
                    </AppButton>
                </>
            } >

            <div className="LStudio__app_priority_toggle">
                <FieldSwitch id="personal_vault_group_isFavorite" label="Destacado" text="Mostrar este grupo primero"
                    isChecked={!!FORM.values.isFavorite}
                    onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
            </div>

            <FormGrid columns={3}>
                <FieldInput {...FORM.field("name")} label="Nombre" placeholder="Ej: Google, Banco, Suscripciones" />

                <FieldSelect {...FORM.field("kind")} label="Tipo" options={OPTIONS_VAULT_KIND} />

                <FieldInput {...FORM.field("url")} label="Sitio o panel" placeholder="Ej: https://accounts.google.com" />

                <FieldInput {...FORM.field("accessPin")} type="pin" label="Pin de acceso"
                    placeholder={IS_EDIT ? "Dejalo vacío para no cambiarlo" : "Mínimo 4 caracteres"}
                    hint={IS_EDIT && group.hasAccessPin ? "Este grupo ya tiene PIN. Se guarda como hash irreversible." : "Opcional. Recomendado para grupos muy sensibles; se guarda como hash irreversible."} />

                <FieldColor {...FORM.field("color")} label="Color" swatches={PALETTE_HEX} />

                <FieldTextarea {...FORM.field("description")} label="Descripción" rows={2} span={3}
                    placeholder="Ej: Cuentas personales y métodos de recuperación" />

                <FieldRichText {...FORM.fieldValue("notes", { emptyValue: "" })}
                    label="Notas personales" minHeight={180}
                    placeholder="Qué guarda este grupo, recuperación, datos importantes…" span={3} />
            </FormGrid>
        </AppModal>
    );
};
